import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../../store';
import { motion } from 'framer-motion';

const TOTAL_DURATION = 12000;
const STEP_DELAY = 2200;

const ANALYSIS_STEPS = [
  {
    title: 'Analyzing your face data',
    items: [
      { text: '3 standout traits detected', color: 'text-green-400', icon: 'star' },
      { text: '4 areas that could age you faster', color: 'text-yellow-400', icon: 'warn' },
      { text: '1 high-priority fix flagged', color: 'text-red-400', icon: 'alert' },
    ],
  },
  {
    title: 'Calculating your attractiveness baseline',
    items: [],
  },
];

export function AnalyzingScreen() {
  const navigate = useNavigate();
  const { setIsAnalyzing, nextStep, rating } = useStore();
  const [activeStep, setActiveStep] = useState(0);
  const [activeItem, setActiveItem] = useState(0);

  useEffect(() => {
    const startTimeout = setTimeout(() => setActiveItem(1), STEP_DELAY);
    const secondTimeout = setTimeout(() => setActiveItem(2), STEP_DELAY * 2);
    const stepTimeout = setTimeout(() => {
      setActiveStep(1);
      setActiveItem(0);
    }, STEP_DELAY * 3);
      const finishTimeout = setTimeout(() => {
      setIsAnalyzing(false);
      nextStep();
      navigate('/paywall');
    }, TOTAL_DURATION);

    return () => {
      clearTimeout(startTimeout);
      clearTimeout(secondTimeout);
      clearTimeout(stepTimeout);
      clearTimeout(finishTimeout);
    };
  }, [navigate, nextStep, setIsAnalyzing]);

  const backgroundImage = rating.frontPhotoPreview;

  return (
    <div className="min-h-screen relative bg-black overflow-hidden">
      {backgroundImage && (
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url(${backgroundImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'brightness(0.55) blur(2px)',
            transform: 'scale(1.02)',
          }}
        />
      )}

      <div className="relative z-10 min-h-screen flex flex-col px-6 py-10 max-w-md mx-auto">
        <h1 className="text-3xl font-bold text-white mb-6">
          Building your looks profile...
        </h1>

        <div className="bg-black/55 border border-white/10 rounded-3xl p-6 backdrop-blur-sm">
          {ANALYSIS_STEPS.map((step, stepIndex) => (
            <div key={step.title} className="mb-6 last:mb-0">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-7 h-7 rounded-full border border-white/30 flex items-center justify-center">
                  <svg
                    className="w-4 h-4 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h2 className="text-lg font-semibold text-white">{step.title}</h2>
              </div>

              {step.items.length > 0 && (
                <div className="space-y-3 pl-10">
                  {step.items.map((item, index) => {
                    const isVisible =
                      stepIndex < activeStep ||
                      (stepIndex === activeStep && index <= activeItem);
                    return (
                      <motion.div
                        key={item.text}
                        initial={{ opacity: 0, x: -6 }}
                        animate={{ opacity: isVisible ? 1 : 0, x: isVisible ? 0 : -6 }}
                        transition={{ duration: 0.3 }}
                        className={`flex items-center gap-3 ${item.color}`}
                      >
                        {item.icon === 'star' && (
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 2l2.8 6.7 7.2.6-5.4 4.7 1.6 7-6.2-3.7-6.2 3.7 1.6-7L2 9.3l7.2-.6L12 2z" />
                          </svg>
                        )}
                        {item.icon === 'warn' && (
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 2L1 21h22L12 2zm1 15h-2v-2h2v2zm0-4h-2V9h2v4z" />
                          </svg>
                        )}
                        {item.icon === 'alert' && (
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 2a10 10 0 100 20 10 10 0 000-20zm5 13l-1.4 1.4L12 12.8 8.4 16.4 7 15l3.6-3.6L7 7.8 8.4 6.4 12 10l3.6-3.6L17 7.8 13.4 11.4 17 15z" />
                          </svg>
                        )}
                        <span className="text-base">{item.text}</span>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
